import axios from 'axios';
import {
  addTranslationForLanguage,
  initialize,
  setActiveLanguage,
} from 'react-localize-redux';
import * as Redux from 'redux';

import { ASSETS_BASE_URL, BUILD_NUM } from '../../BuildConstants';
import { fdApi } from '../../core/api';
import { analyticsService } from '../../core/services';
import { logger } from '../../core/services/loggerService';
import {
  activeLocaleInRedux,
  availableLanguages,
  DEFAULT_LOCALE_CODE,
  formatLanguageJSON,
} from '../../localization';
import DEFAULT_TRANSLATION from '../../localization/en.json';
import { addTime } from '../../utils/dateTimeUtils';
import { deliveryKey } from '../../utils/makeUtils';
import * as analytics from '../analytics';
import * as Api from '../Api';
import { cookieConsent as cookieConsentPopup } from '../cookieconsent';
import * as Data from '../Data';
import { setCookieConsent } from '../redux/cookie-consent/actions';
import { Routes } from '../Routes';
import { storage } from '../services/storageServices';
import * as Store from '../Store';
import { Utilities } from '../Utilities';
import { AddLocation, SetClientAnalytics, SetCountryCode } from './Actions';

interface IpInfo {
  ip: string;
  hostname: string;
  city: string;
  region: string;
  country: string;
  loc: string;
  org: string;
}

type QueryParams = {
  count: number;
  includePhysicalRestaurantIds?: string;
  Latitude: number;
  Longitude: number;
  skip: number;
};

const applyCallbackToMenu = (
  data: Api.RestaurantSummaryCommon[],
  callbackFn?: (vrid: number) => void,
) => {
  if (data.length === 0) {
    return;
  }

  if (callbackFn) {
    const vrid = data[0].VirtualRestaurantId;
    callbackFn(vrid);
  }
};

export const FindInitialRestaurant =
  (then?: (vrid: number) => any, _iteration?: number) =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const {
      AppConfig,
      Config,
      Location: { geo },
    } = getState().flipdish;

    const q: QueryParams = {
      count: 1000,
      Latitude: geo.Latitude,
      Longitude: geo.Longitude,
      skip: 0,
    };

    if (Config.IncludePhysicalRestaurantIds) {
      q.includePhysicalRestaurantIds = Config.IncludePhysicalRestaurantIds;
    }

    if (AppConfig.status === 'success' && AppConfig.data.PickupEnabled) {
      await fdApi
        .use(fdApi.store.getCollectionSummaries({ params: q }))
        .then((data) => {
          dispatch({
            type: 'SET_COLLECTION_SUMMARIES',
            payload: {
              status: 'success',
              data,
            },
          });
          applyCallbackToMenu(data as Api.RestaurantSummaryCommon[], then);
        })
        .catch((_error) => {
          dispatch({
            type: 'SET_COLLECTION_SUMMARIES',
            payload: {
              status: 'none',
            },
          });
        });
    }

    if (AppConfig.status === 'success' && AppConfig.data.DeliveryEnabled) {
      const deliveryParams = {
        count: 1000,
        deviceId: Config.DeviceId,
        Latitude: geo.Latitude,
        Longitude: geo.Longitude,
        skip: 0,
      };

      await fdApi
        .use(fdApi.store.getDeliverySummaries({ params: deliveryParams }))
        .then((data) => {
          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'success',
              data,
            },
          });

          applyCallbackToMenu(data as Api.RestaurantSummaryCommon[], then);
        })
        .catch((_error) => {
          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'none',
            },
          });
        });
    }
  };

export const setupLanguage =
  (languageToSet: string) => async (dispatch: Redux.Dispatch<Redux.Action>) => {
    let locale = DEFAULT_LOCALE_CODE;
    let translatedJson;
    let language = languageToSet;
    // Override language choice if customer has set the language
    // manually from change language.
    const customerLanguage = Utilities.getLangauageFromStorage();
    if (customerLanguage && customerLanguage !== language) {
      language = customerLanguage;
    }
    if (language === DEFAULT_LOCALE_CODE) {
      translatedJson = DEFAULT_TRANSLATION;
    } else {
      try {
        const languages = [language];
        const [languagePrefix] = language.split('-');
        const similarLanguage = availableLanguages.find(
          (l) => l.startsWith(languagePrefix) && l !== language,
        );
        if (similarLanguage) {
          languages.push(similarLanguage);
        }
        const responses = await Promise.all(
          languages.map(async (l) => {
            if (l === DEFAULT_LOCALE_CODE) {
              return DEFAULT_TRANSLATION;
            }
            const response = await fetch(
              // @ts-ignore its set in main.js
              `${ASSETS_BASE_URL}static/languages/${l}.json?version=${BUILD_NUM}`,
            );
            return response.json();
          }),
        );
        const formattedLanguage = formatLanguageJSON(responses);
        locale = language;
        translatedJson = formattedLanguage;
      } catch (e) {
        logger.error(
          `Something went wrong while downloading translations for ${language}`,
          {
            message: e.message,
            stack: e.stack,
          },
        );
        locale = DEFAULT_LOCALE_CODE;
        translatedJson = DEFAULT_TRANSLATION;
        // Remove user language from local storage if
        // there is error in loading language.
        if (customerLanguage) {
          Utilities.removeLangauageFromStorage();
        }
      }
    }
    dispatch(initialize([locale]));
    dispatch(
      addTranslationForLanguage(
        Utilities.translateTransform(translatedJson),
        locale,
      ),
    );
    dispatch(setActiveLanguage(locale));
    axios.defaults.headers.common['Flipdish-Language'] = locale;
  };

export const LoadAppconfig =
  () =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const dispatchThunk = dispatch as any;
    dispatchThunk(LoadGeoIpLookup());

    const { locale } = getState();
    await fdApi
      .use(fdApi.whitelabel.getAppConfig())
      .then((appConfig: Api.AppConfig) => {
        // Set legal docs urls
        const CookiePolicyUrl =
          Utilities.extraOption('data-cookie-policy-link') ||
          appConfig.CookiePolicyUrl ||
          'https://www.flipdish.com/cookie-policy/';
        const TermsAndConditionsUrl =
          Utilities.extraOption('data-terms-link') ||
          appConfig.TermsAndConditionsUrl ||
          'https://www.flipdish.com/docs/tac/';
        const PrivacyPolicyUrl =
          Utilities.extraOption('data-privacy-policy-link') ||
          appConfig.PrivacyPolicyUrl ||
          'https://www.flipdish.com/privacy-policy/';

        const { Offset: attrOffset } = getState().flipdish;
        const apiOffset = appConfig.DataOffset;
        const htmlAttrOffsetOrDefault = attrOffset || 0;
        const viewportTopOffset =
          apiOffset >= 0 ? apiOffset : htmlAttrOffsetOrDefault;
        const languageFromConfig =
          appConfig.Languages.length > 0
            ? appConfig.Languages[0]?.LangageId || ''
            : '';

        let activelanguage = activeLocaleInRedux(locale.languages);
        const customAppConfig = {
          ...appConfig,
          CookiePolicyUrl,
          TermsAndConditionsUrl,
          PrivacyPolicyUrl,
          ViewportTopOffset: viewportTopOffset,
        };

        if (
          languageFromConfig !== '' &&
          languageFromConfig !== activelanguage
        ) {
          activelanguage = languageFromConfig;
          dispatchThunk(setupLanguage(languageFromConfig));
        }

        Utilities.setInLocalStorage('flipdish-app-config', customAppConfig);

        dispatchThunk(
          AddLocation(
            {
              Latitude: customAppConfig.MapCenterLatitude,
              Longitude: customAppConfig.MapCenterLongitude,
            },
            Api.LocationCertainty.AppConfig,
          ),
        );

        dispatch({
          type: 'LOAD_APP_CONFIG',
          payload: customAppConfig,
        });

        const disableNonEssentialCookies = customAppConfig.Features.includes(
          'disable-non-essential-cookies',
        );
        const isCookieConsentRequired =
          customAppConfig.ComplianceType ===
            Api.ComplianceType.GdprCompliance ||
          ('ComplianceType' in customAppConfig === false &&
            customAppConfig.Features.includes('enable-gdpr-cookies'));

        const setupGA = (disable: boolean): void =>
          analytics.initialize(
            customAppConfig.GoogleAnalyticsTokens.map((trackingId) => [
              trackingId,
              {},
            ]),
            disable,
          );

        const analyticsSettings = {
          facebookIds: customAppConfig.FacebookTokens,
          uiVersion: customAppConfig.WebOrderUiVersion,
        };

        if (disableNonEssentialCookies) {
          setupGA(disableNonEssentialCookies);
        } else if (isCookieConsentRequired) {
          const restaurantName = customAppConfig.BrandName;
          const logoUrl = customAppConfig.BrandLogoUrl;

          cookieConsentPopup.init(
            {
              lang: activelanguage,
              logoUrl,
              restaurantName,
              cookiePolicyUrl: customAppConfig.CookiePolicyUrl || '',
            },
            (userCookieSettings) => {
              const isPerformanceEnabled =
                userCookieSettings.includes('performance');

              analyticsService.initialise(
                analyticsSettings,
                userCookieSettings,
              );

              // Google Analytics
              setupGA(!isPerformanceEnabled);

              dispatch(
                setCookieConsent(
                  userCookieSettings as WebOrder.State.CookieConsent['selectedGroups'],
                ),
              );
            },
          );
        } else {
          analyticsService.initialise(analyticsSettings);
          setupGA(disableNonEssentialCookies);
        }

        // If client analytics is not present it will still set ClientAnalytics
        // to true so GA sends event's to Flipdish's Google Analytics
        dispatchThunk(SetClientAnalytics(true));
      });
  };

export const LoadCollectionDetails =
  (prid: number, refresh: boolean) =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const dispatchCollection = (
      r: Data.Async<Api.CollectionRestaurantDetail>,
    ) => {
      dispatch({
        type: 'SET_RESTAURANT_COLLECTION',
        payload: {
          prid,
          restaurant: r,
        },
      });
    };

    const pickupRestaurantJson =
      storage.getItem<Api.CollectionRestaurantDetail>(
        `flipdish-pickup-restaurant-${prid}`,
      );

    const restaurant = getState().collectionRestaurants[prid];
    const isRestaurantLoaded = restaurant && restaurant.status === 'success';

    /* Get from localStorage if it exists in localStorage, is not in redux store
    and is on an initial pass (hence new data from API is not required) */
    if (pickupRestaurantJson && !isRestaurantLoaded && !refresh) {
      const cacheData = pickupRestaurantJson;
      const customData = {
        ...cacheData,
        ClosingTimeStamp: undefined,
        FromAPI: false,
      } as Api.CollectionRestaurantDetail;

      dispatchCollection({ status: 'success', data: customData });
    }

    await fdApi
      .use(fdApi.store.getCollectionDetail(`${prid}`))
      .then((data: Api.CollectionRestaurantDetail) => {
        Utilities.setInLocalStorage(`flipdish-pickup-restaurant-${prid}`, data);

        const closingTimeStamp = data?.SecondsUntilRestaurantCloses
          ? addTime(new Date(), 'seconds', data.SecondsUntilRestaurantCloses)
          : undefined;

        const customData = {
          ...(data as object),
          ClosingTimeStamp: closingTimeStamp,
          FromAPI: true,
        } as Api.CollectionRestaurantDetail;

        dispatchCollection({
          data: customData,
          status: 'success',
        });
      })
      .catch((_error) => {
        if (!pickupRestaurantJson) {
          dispatchCollection({ status: 'none' });
        }
      });
  };

export const LoadDeliveryDetails =
  (vrId: number, deliveryLocationId: number, refresh = false) =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const key = deliveryKey(vrId, deliveryLocationId);
    const Restaurant = getState().deliveryRestaurants[key];

    const restaurantExistsFromApi =
      Restaurant && Restaurant.status === 'success';

    if (restaurantExistsFromApi && !refresh) {
      return;
    }

    const query = {
      deviceId: getState().flipdish.Config.DeviceId,
      deliveryLocationId,
    };
    const options = {
      params: query,
      retry: 3,
      retryDelay: 1000,
    };

    await fdApi
      .use(fdApi.store.getDeliveryDetail(vrId, options))
      .then((data: Api.DeliveryRestaurantDetail) => {
        const closingTimeStamp = data?.SecondsUntilRestaurantCloses
          ? addTime(new Date(), 'seconds', data.SecondsUntilRestaurantCloses)
          : undefined;

        const dataFromApi = {
          ...data,
          ClosingTimeStamp: closingTimeStamp,
          FromAPI: true,
        };

        dispatch({
          type: 'SET_RESTAURANT_DELIVERY',
          payload: {
            dlid: deliveryLocationId,
            restaurant: { status: 'success', data: dataFromApi },
            vrid: vrId,
          },
        });
      })
      .catch(() => {
        dispatch({
          type: 'SET_RESTAURANT_DELIVERY',
          payload: {
            dlid: deliveryLocationId,
            restaurant: { status: 'none' },
            vrid: vrId,
          },
        });
      });
  };

export const LoadMenuByUrl =
  (menuUrl: string) =>
  (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    let { MenuData } = getState().flipdish;
    const pending: Store.MenuDataPanding = {
      type: 'MENU_DATA_PENDING',
      menuKey: menuUrl,
    };

    let existingMenuData = MenuData[menuUrl];

    // If menu doesn't exist in the redux store dispatch pending and get menu from localStorage if it exists
    if (!existingMenuData) {
      dispatch(pending);

      const menuJson = storage.getItem<Api.MenuReposnse>(menuUrl);

      if (menuJson) {
        menuJson.FromAPI = false;

        const action: Store.AddMenuData = {
          type: 'ADD_MENU_DATA',
          menuKey: menuUrl,
          menu: menuJson,
        };

        dispatch(action);
      }
    }

    // Check for if the menu data in the redux store has been got from the API
    let existingMenuFromAPI = false;

    MenuData = getState().flipdish.MenuData;
    existingMenuData = MenuData[menuUrl];

    if (existingMenuData && existingMenuData.status === 'success') {
      existingMenuFromAPI = existingMenuData.data.FromAPI;
    }

    if (existingMenuFromAPI) {
      return;
    }

    axios({
      method: 'get',
      url: menuUrl,
      withCredentials: false,
    }).then((response) => {
      const menu: any = response.data;
      menu.FromAPI = true;

      menu.MenuSections = Data.Sorted(menu.MenuSections);
      Utilities.setInLocalStorage(menuUrl, menu);

      const action: Store.AddMenuData = {
        type: 'ADD_MENU_DATA',
        menuKey: menuUrl,
        menu,
      };

      dispatch(action);
    });
  };

export const LoadRestaurant = (props: Routes) => {
  const { params } = props;

  const vrId = parseInt(`${params.vrid}`);
  // params.id can equal physicalRestaurantId and deliveryLocationId depending on the route
  const prId = params.id ? parseInt(`${params.id}`) : undefined;
  const deliveryLocationId = prId;
  const deliveryType = params.type;

  return (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const dispatchThunk = dispatch as any;

    const { CollectionSummaries, DeliverySummaries } = getState().flipdish;

    let Summary: Api.RestaurantSummaryCommon;
    switch (deliveryType) {
      case 'collection': {
        if (CollectionSummaries.status === 'success' && prId) {
          Summary = CollectionSummaries.data.find(
            (s) =>
              s.VirtualRestaurantId === vrId && s.PhysicalRestaurantId === prId,
          );

          dispatchThunk(LoadCollectionDetails(prId, false));
        }
        break;
      }
      case 'delivery': {
        if (DeliverySummaries.status === 'success' && deliveryLocationId) {
          Summary = DeliverySummaries.data.find(
            (s) => s.VirtualRestaurantId === vrId,
          );

          dispatchThunk(LoadDeliveryDetails(vrId, deliveryLocationId, true));
        }
        break;
      }
      default:
        // why this!!! idk but was like this before
        const getSummary = (
          summaries: Data.Async<Api.RestaurantSummaryCommon[]>,
        ) =>
          summaries.status === 'success' &&
          summaries.data.find((s) => s.VirtualRestaurantId === vrId);

        if (
          CollectionSummaries.status === 'success' &&
          CollectionSummaries.data.length > 0
        ) {
          Summary = getSummary(CollectionSummaries);

          dispatchThunk(
            LoadCollectionDetails(Summary.PhysicalRestaurantId, false),
          );
        } else if (DeliverySummaries.status === 'success') {
          Summary = getSummary(DeliverySummaries);

          dispatchThunk(LoadDeliveryDetails(vrId, deliveryLocationId, true));
        }
        break;
    }

    // If summary load menu
    if (Summary) {
      Utilities.loadMenu([Summary], dispatch, getState, false);
    }
  };
};

export const getRestaurantData =
  (vrId: number, orderType: string, id: number) =>
  (dispatch: Redux.Dispatch<Redux.Action>, _getState: () => Store.State) => {
    const dispatchThunk = dispatch as any;
    if (orderType === 'collection') {
      dispatchThunk(LoadCollectionDetails(id, false));
    } else {
      dispatchThunk(LoadDeliveryDetails(vrId, id, true));
    }
  };

export const LoadGeoIpLookup =
  () => async (dispatch: Redux.Dispatch<Redux.Action>) => {
    const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';
    const isOperaMini = navigator.userAgent.indexOf('Opera Mini') > -1;
    if (isDev || isOperaMini) {
      return;
    }

    const commonHeaders = { ...axios.defaults.headers.common };
    delete commonHeaders['X-Flipdish-Token'];
    delete commonHeaders['X-Coordinates'];
    delete commonHeaders['Flipdish-App-Type'];
    delete commonHeaders['Flipdish-White-Label-Id'];

    await axios
      .get('/json', {
        baseURL: 'https://ipinfo.io',
        headers: {
          common: commonHeaders,
        },
        withCredentials: false,
      })
      .then((data) => {
        const result: IpInfo = data.data;
        const [lat, lng] = result.loc.split(',').map(parseFloat);

        const geo = { Latitude: lat, Longitude: lng };

        dispatch(SetCountryCode(result.country));
        dispatch(AddLocation(geo, Api.LocationCertainty.GeoIp));
      })
      .catch(() => {
        console.log('Error looking up ip address on ipinfo.io');
      });
  };

export const getLocationAreaData =
  (prid: number) => async (dispatch: Redux.Dispatch<Redux.Action>) => {
    await fdApi.use(fdApi.store.getLocationAreaData(prid)).then((data) => {
      dispatch({
        type: 'SET_RESTAURANT_COLLECTION_LOCATION_AREAS',
        payload: {
          locationAreas: data,
          prid,
        },
      });
    });
  };
